'use client';

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useTranslations } from 'next-intl';
import PulseOmicsRing from 'components/pulsomics/pulseOmics/PulseOmicsRing';
import ButtonV2 from 'components/shared/ButtonV2';

import { scienceLandingAsset } from './asset';
import { useReveal } from './useReveal';
import styles from './ThreePillars.module.css';

/* "Where the science lives." — three pillars (Figma 900:249; hover set 861:911).
   Each card blurs its image on hover and forms a frosted widget:
   - Research Studies → a signal arc; the active chip advances every 2s (moving the
     list up along the curve). Runs while hovered, remembers its place on leave.
   - Science of aging → the card flips in (Figma 1156:2036), the Ultra Age
     number turns like a pair of counter-rotating dials (tens up, ones down) a
     few clicks from a random start and settles on 34, the "2 years younger" chip lands,
     then the cardio and blood age rows list in one by one (values static — only the
     headline age animates).
   - Pulsomics → the live PulseOmicsRing (recoloured white to match Figma). */

const CHIPS = [
  { labelKey: 'sleep', chip: 'sleep' },
  { labelKey: 'heart', chip: 'heart' },
  { labelKey: 'womens', chip: 'womens' },
  { labelKey: 'metabolic', chip: 'metabolic' },
  { labelKey: 'activity', chip: 'activity' },
  { labelKey: 'steps', chip: 'steps' },
  { labelKey: 'temperature', chip: 'temperature' },
  { labelKey: 'bloodOxygen', chip: 'blood-oxygen' },
  { labelKey: 'longevity', chip: 'longevity' },
  { labelKey: 'environment', chip: 'environment' },
] as const;
/* the arc shows 11 slots (5 either side of the active one); the categories run
   twice round the loop so every slot is filled and the wrap happens off-card */
const LOOP = [...CHIPS, ...CHIPS];
const N = LOOP.length;
/* left edge (px) by distance from the active/centre slot — the arc bulges right */
const LEFT_BY_DIST = [129, 125, 117, 102, 83, 59];
const ALPHA_BY_DIST = [1, 0.6, 0.5, 0.42, 0.34, 0.26];

/* signed offset of i from active, wrapped into [-N/2, N/2] so the list scrolls */
const wrapOffset = (i: number, active: number) => {
  let d = i - active;
  if (d > N / 2) d -= N;
  if (d < -N / 2) d += N;
  return d;
};

const PILLAR_IMG = {
  studies: scienceLandingAsset('pillars/pillar-studies-ascii2.jpg'),
  aging: scienceLandingAsset('pillars/pillar-aging-ascii2.jpg'),
  pulsomics: scienceLandingAsset('pillars/pillar-pulsomics-ascii2.jpg'),
} as const;

/* Science of aging card: an Ultra Age reading. Ultra Age is the average of
   Cardio Age (the VO2max-based age), Blood Age, and Brain Age. Pulse Age is
   not shown. The chip and the row deltas use the same actual age, so every
   number agrees: 33, 35 and 34 average to 34, two years under 36. */
const ACTUAL_AGE = 36;
const AGE_SYSTEMS = [
  { k: 'Cardio Age', v: 33 },
  { k: 'Blood Age', v: 35 },
  { k: 'Brain Age', v: 34 },
] as const;
const AGE = Math.round(AGE_SYSTEMS.reduce((sum, r) => sum + r.v, 0) / AGE_SYSTEMS.length);
const YOUNGER = ACTUAL_AGE - AGE;
const delta = (v: number) => (v < ACTUAL_AGE ? `−${ACTUAL_AGE - v}` : `+${v - ACTUAL_AGE}`);

/* The headline age as counter-rotating dials: each digit is a column of 0–9
   repeated three times (so it can roll through the wrap), and neighbouring
   digits wheel in OPPOSITE directions — tens up, ones down — each the short way
   from `from`'s digit to `to`'s. The slide itself is CSS (it runs when the card
   first opens — [data-played]); timing comes from the parent's class. */
function Odometer({ from, to, className }: { from: number; to: number; className?: string }) {
  const len = Math.max(String(from).length, String(to).length);
  const a = String(from).padStart(len, '0');
  const b = String(to).padStart(len, '0');
  return (
    <span className={`${styles.odo} ${className ?? ''}`}>
      {Array.from(b).map((ch, k) => {
        const s = Number(a[k]);
        const e = Number(ch);
        const end = 10 + e;
        // even digits wheel up to their target, odd ones wheel down
        const start = k % 2 === 0 ? end - ((e - s + 10) % 10) : end + ((s - e + 10) % 10);
        return (
          <span key={k} className={styles.odoCol}>
            <span className={styles.odoStrip} style={{ '--s': start, '--e': end } as CSSProperties}>
              {DIGITS}
            </span>
          </span>
        );
      })}
    </span>
  );
}
const DIGITS = Array.from({ length: 30 }, (_, n) => <span key={n}>{n % 10}</span>);

export default function ThreePillars() {
  const t = useTranslations('science');
  // eyebrow + heading, then the three cards, fade up as they enter
  const revealRef = useReveal<HTMLElement>();
  // Desktop opens a card on hover. Phones have no hover, so the same open
  // state plays on its own — no press-and-hold.
  const [canHover, setCanHover] = useState(true);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 992px) and (hover: hover) and (pointer: fine)');
    const sync = () => setCanHover(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  /* long-press used to preview the hover art on touch. Phones now play that
     same open state on their own, so a press is not required. */
  const [studiesHover, setStudiesHover] = useState(false);
  const [agingHover, setAgingHover] = useState(false);
  const [ringOn, setRingOn] = useState(false);
  useEffect(() => {
    setStudiesHover(!canHover);
    setAgingHover(!canHover);
    setRingOn(!canHover);
  }, [canHover]);

  // every card's hover art (studies arc, aging card, pulsomics ring) is
  // authored in the Figma 464×560 tile; publish --card-scale on each tile so it
  // all scales with the tile's real size — narrow desktops, iPads, phones — and
  // never crowds or overflows the card. Fits by the tighter of width and height.
  const studiesMediaRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return undefined;
    const tiles = Array.from(grid.querySelectorAll<HTMLElement>('[data-variant]'));
    const update = () => {
      for (const el of tiles) {
        const k = Math.min(el.clientWidth / 464, el.clientHeight / 560);
        el.style.setProperty('--card-scale', k.toFixed(4));
      }
    };
    update();
    const ro = new ResizeObserver(update);
    tiles.forEach((el) => ro.observe(el));
    return () => ro.disconnect();
  }, []);

  const [active, setActive] = useState(5); // AFib detection to start

  useEffect(() => {
    if (!studiesHover) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    /* first advance after 0.3s, then every 1.2s — a short hold between glides so
       the list reads as one continuous flow rather than step, wait, step */
    let interval: ReturnType<typeof setInterval> | undefined;
    const start = setTimeout(() => {
      setActive((a) => (a + 1) % N);
      interval = setInterval(() => setActive((a) => (a + 1) % N), 1200);
    }, 300);
    return () => {
      clearTimeout(start);
      if (interval) clearInterval(interval);
    };
  }, [studiesHover]);

  /* the reveal plays ONCE — the first time the card opens; after that the card
     flips in with the values already in place. The whole sequence is CSS, timed
     off [data-played]. */
  const [agingPlayed, setAgingPlayed] = useState(false);
  useEffect(() => {
    if (agingHover) setAgingPlayed(true);
  }, [agingHover]);
  /* the dials start a few steps off 34 — each digit 4–6 clicks away, tens below
     (it wheels up), ones above (it wheels down) — picked on the client, so SSR
     and hydration agree on the fixed fallback */
  const [ageFrom, setAgeFrom] = useState(98);
  useEffect(() => {
    const step = () => 4 + Math.floor(Math.random() * 3);
    const tens = (Math.floor(AGE / 10) - step() + 10) % 10;
    const ones = ((AGE % 10) + step()) % 10;
    setAgeFrom(tens * 10 + ones);
  }, []);

  // Desktop hovers to play a card's interaction. On touch the open state is
  // already on, so there is nothing to press.
  const intentTimers = useRef(new Map<Dispatch<SetStateAction<boolean>>, ReturnType<typeof setTimeout>>());
  useEffect(() => {
    const timers = intentTimers.current;
    return () => timers.forEach((id) => clearTimeout(id));
  }, []);
  const bind = (set: Dispatch<SetStateAction<boolean>>) =>
    canHover
      ? {
          onMouseEnter: () => {
            clearTimeout(intentTimers.current.get(set));
            intentTimers.current.set(set, setTimeout(() => set(true), 80));
          },
          onMouseLeave: () => {
            clearTimeout(intentTimers.current.get(set));
            set(false);
          },
        }
      : {};

  return (
    <section ref={revealRef} className={styles.section} aria-label="Where the science lives">
      <div className={styles.inner}>
        <header className={styles.head}>
          <p className={styles.eyebrow} data-reveal>
            {t('science.landing.pillars.eyebrow')}
          </p>
          <h2 className={styles.heading} data-reveal>
            {t('science.landing.pillars.title')}
          </h2>
        </header>

        <div ref={gridRef} className={styles.grid}>
          {/* ── Studies — signal arc, active advances every 2s ── */}
          <article className={styles.card} data-reveal>
            {/* the art is not a link on any screen — only "Learn more" navigates */}
            <div
              ref={studiesMediaRef}
              className={styles.media}
              data-variant="studies"
              data-active={studiesHover ? 'true' : undefined}
              aria-label={t('science.landing.pillars.studies.imageAlt')}
              {...bind(setStudiesHover)}
            >
              <span
                className={styles.mediaImg}
                style={{ backgroundImage: `url(${PILLAR_IMG.studies})` }}
              />
              {/* the blurred copy fades in over the sharp image — an opacity
                  crossfade the GPU composites, instead of re-blurring per frame */}
              <span
                className={styles.mediaImgBlur}
                style={{ backgroundImage: `url(${PILLAR_IMG.studies})` }}
                aria-hidden="true"
              />
              <span className={styles.dim} aria-hidden="true" />
              {/* dotted guide — to the LEFT of the chip arc and slightly shorter */}
              <svg className={styles.arcLine} viewBox="0 0 464 560" preserveAspectRatio="none" aria-hidden="true">
                <path
                  d="M40 0 C 96 160, 96 400, 40 560"
                  fill="none"
                  stroke="rgba(255,255,255,0.4)"
                  strokeWidth="1"
                  strokeDasharray="1.5 6"
                  strokeLinecap="round"
                />
              </svg>
              <span className={styles.arc} aria-hidden="true">
                {LOOP.map((c, i) => {
                  const off = wrapOffset(i, active);
                  const dist = Math.min(Math.abs(off), 5);
                  const activeChip = off === 0;
                  const left = LEFT_BY_DIST[dist];
                  /* position by the chip's VERTICAL CENTRE (translateY(-50%)) with
                     a constant step, so the gap above and below the enlarged
                     active chip comes out equal by construction. */
                  const centerY = 280 + off * 72;
                  const rot = off * 5.6;
                  /* past the 5th slot an item is off the arc (faded out); at the
                     wrap boundary it teleports top↔bottom, so kill its transition
                     there so it never slides across the card */
                  const hidden = Math.abs(off) > 5;
                  const edge = Math.abs(off) >= N / 2 - 1;
                  /* transform-only placement (no left/top) so each step is a
                     compositor move, not a layout pass */
                  return (
                    <span
                      key={`${c.chip}-${i}`}
                      className={`${styles.sig} ${activeChip ? styles.sigActive : ''}`}
                      style={{
                        transform: `translate3d(${left}px, ${centerY}px, 0) translateY(-50%) rotate(${rot}deg)`,
                        opacity: hidden ? 0 : 1,
                        transition: edge ? 'none' : undefined,
                      }}
                    >
                      <span className={styles.sigThumbBox}>
                      <span
                        className={styles.sigThumb}
                        style={{ backgroundImage: `url(${scienceLandingAsset(`pillars/chips/${c.chip}.jpg`)})`, opacity: activeChip ? 1 : ALPHA_BY_DIST[dist] + 0.2 }}
                      />
                      </span>
                      <span className={styles.sigLabel} style={{ color: `rgba(255,255,255,${activeChip ? 1 : ALPHA_BY_DIST[dist]})` }}>
                        {t(`science.landing.pillars.chips.${c.labelKey}`)}
                      </span>
                    </span>
                  );
                })}
              </span>
            </div>
            <div className={styles.body}>
              <h3 className={styles.title}>{t('science.landing.pillars.studies.title')}</h3>
              <p className={styles.desc}>{t('science.landing.pillars.studies.body')}</p>
              <ButtonV2
                href="/science/studies/"
                variant="transparentDark"
                label={t('science.landing.pillars.learnMore')}
              />
            </div>
          </article>

          {/* ── Science of aging — flip + count-up + reveal ── */}
          <article className={styles.card} data-reveal>
            <div
              className={styles.media}
              data-variant="aging"
              data-active={agingHover ? 'true' : undefined}
              data-played={agingPlayed ? 'true' : undefined}
              aria-label={t('science.landing.pillars.aging.imageAlt')}
              {...bind(setAgingHover)}
            >
              <span
                className={styles.mediaImg}
                style={{ backgroundImage: `url(${PILLAR_IMG.aging})` }}
              />
              {/* the blurred copy fades in over the sharp image — an opacity
                  crossfade the GPU composites, instead of re-blurring per frame */}
              <span
                className={styles.mediaImgBlur}
                style={{ backgroundImage: `url(${PILLAR_IMG.aging})` }}
                aria-hidden="true"
              />
              <span className={styles.dim} aria-hidden="true" />
              <span className={styles.flip}>
                <span className={`${styles.widget} ${styles.agingCard}`} aria-hidden="true">
                  <span className={styles.agingHead}>
                    <span className={styles.agingLabel}>Your Ultra Age</span>
                    <span className={styles.agingValue}>
                      <Odometer from={ageFrom} to={AGE} className={styles.agingBig} />
                      <span className={styles.agingUnit}>yrs</span>
                    </span>
                    <span className={styles.agingChip}>
                      {YOUNGER} years younger
                      <svg width="6.5" height="6.5" viewBox="0 0 6.32 6.32" fill="none">
                        <path
                          d="M3.166 1.052v4.214M5.273 3.159 3.166 5.266 1.059 3.159"
                          stroke="currentColor"
                          strokeWidth="0.79"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </span>
                  <span className={styles.agingRows}>
                    {AGE_SYSTEMS.map((r, i) => (
                      <span key={r.k} className={styles.agingRow} style={{ '--i': i } as CSSProperties}>
                        <span className={styles.agingRowK}>{r.k}</span>
                        <span className={styles.agingRowD}>{delta(r.v)}</span>
                        <span className={styles.agingRowV}>{r.v}</span>
                      </span>
                    ))}
                  </span>
                </span>
              </span>
            </div>
            <div className={styles.body}>
              <h3 className={styles.title}>{t('science.landing.pillars.aging.title')}</h3>
              <p className={styles.desc}>{t('science.landing.pillars.aging.body')}</p>
              {/* not live yet: the same pill, but inert — no hover, goes nowhere */}
              <ButtonV2
                variant="transparentDark"
                className={styles.soon}
                tabIndex={-1}
                aria-disabled="true"
                label={t('science.landing.pillars.comingSoon')}
              />
            </div>
          </article>

          {/* ── Pulsomics — the live organ-systems ring (recoloured white) ── */}
          <article className={styles.card} data-reveal>
            <div
              className={styles.media}
              data-variant="ring"
              data-active={ringOn ? 'true' : undefined}
              aria-label={t('science.landing.pillars.pulsomics.imageAlt')}
              {...bind(setRingOn)}
            >
              <span
                className={styles.mediaImg}
                style={{ backgroundImage: `url(${PILLAR_IMG.pulsomics})` }}
              />
              {/* the blurred copy fades in over the sharp image — an opacity
                  crossfade the GPU composites, instead of re-blurring per frame */}
              <span
                className={styles.mediaImgBlur}
                style={{ backgroundImage: `url(${PILLAR_IMG.pulsomics})` }}
                aria-hidden="true"
              />
              <span className={styles.dim} aria-hidden="true" />
              <span className={`${styles.widget} ${styles.ringCard}`}>
                <span className={styles.ringHost}>
                  <PulseOmicsRing compact />
                </span>
              </span>
            </div>
            <div className={styles.body}>
              <h3 className={styles.title}>{t('science.landing.pillars.pulsomics.title')}</h3>
              <p className={styles.desc}>{t('science.landing.pillars.pulsomics.body')}</p>
              <ButtonV2
                href="/pulsomics/"
                variant="transparentDark"
                label={t('science.landing.pillars.learnMore')}
              />
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
